import _get from 'lodash/get';
import { useQuery } from 'react-query';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { getLocalData } from 'utils/localDataStore';
import useContentfulLocale from 'components/useContentfulLocale';
import { useSelector } from 'react-redux';
import { getListsFn } from '../services/getList';

const isOnboardingAllowed = ({
  isOnboardingEnabled,
  brand,
  includedClients,
  excludedClients,
}) => {
  if (!brand || brand === 'none') {
    return false;
  }

  const isNamesEqual = clientName => clientName === brand;

  if (!isOnboardingEnabled && includedClients.length) {
    return includedClients.some(isNamesEqual);
  }

  if (isOnboardingEnabled && excludedClients.length) {
    return !excludedClients.some(isNamesEqual);
  }

  return isOnboardingEnabled;
};

export const useIsOnboardingEnabled = () => {
  const [siteCopy] = useSiteCopySelector(['onboarding-questions']);
  const [siteConfig] = useSiteConfigSelector(['Features']);
  const ctfLocaleFilter = useContentfulLocale();
  const clientDetails = useSelector(state => state.main.clientDetails);
  const brand = getLocalData('brand');
  const isOnboardingEnabled = _get(
    siteConfig,
    ['config', 'isOnboardingEnabled'],
    true,
  );
  const onboardingConfig = _get(siteCopy, 'pageCopy');
  const includedClients = _get(onboardingConfig, 'includedClients', []);
  const excludedClients = _get(onboardingConfig, 'excludedClients', []);
  const listsConfig = _get(onboardingConfig, 'lists', []);
  const clientShortName = _get(clientDetails, 'shortName');
  const minListsCount = _get(onboardingConfig, [
    'minimumClientGoals',
    clientShortName,
  ]);
  const listsIds = listsConfig.map(list => {
    const [listKey] = Object.keys(list).filter(key => key !== 'list-resources');
    return list[listKey];
  });
  const clientExcludedTopicIds = _get(
    clientDetails,
    'excludeTopicCollection.items',
    [],
  ).map(s => s.sys.id);

  const { data: lists = [] } = useQuery(
    !!listsIds.length && [
      'onboarding-lists',
      {
        listsIds,
        clientExcludedTopicIds,
        localeFilters: ctfLocaleFilter,
      },
    ],
    getListsFn,
  );

  return (
    (!minListsCount || lists.length >= minListsCount) &&
    isOnboardingAllowed({
      isOnboardingEnabled,
      brand,
      includedClients,
      excludedClients,
    })
  );
};
